import { defineStore } from 'pinia';
import backendCall from '../services/AxiosService';
import { useToast } from 'primevue/usetoast';

export interface ActiveTimeRecord {
  id: string;
  type: string;
  description?: string;
  startTime: string;
  elapsedTime?: string;
  elapsedSeconds?: number;
  isStartedByCurrentUser?: boolean;
  _aggregatedDescriptions?: string[];
  recordDetails?: {
    request_id?: string;
    equipment_id?: string;
    request_type?: string;
    status?: string;
    isWaitTime?: boolean;
    isTrackProtection?: boolean;
    isStartedByCurrentUser?: boolean;
    description?: string;
    actual_request_id?: string;
    request_number?: string;
    yardCheckId?: string;
    trainId?: string;
    trainIdentifier?: string;
    jobId?: string;
    requestId?: string;
    groupId?: number | string;
    buildId?: number | string;
    gensetNumber?: string;
  };
  equipment_id?: string;
  request_id?: string;
  actual_request_id?: string;
  request_number?: string;
}

export interface EndedTimeRecord {
  id: string;
  type: string;
  trainId?: string | number;
  recordType: string;
  endedAt: Date;
}

export const useTimeTrackingStore = defineStore('timeTracking', {
  state: () => ({
    activeTimeRecords: [] as ActiveTimeRecord[],
    activeTrackProtection: [] as ActiveTimeRecord[], // New: dedicated track protection records
    recentlyEndedRecords: [] as EndedTimeRecord[],
    loading: false,
    autoRefreshInterval: null as NodeJS.Timeout | null,
    timerInterval: null as NodeJS.Timeout | null,
  }),

  actions: {
    async fetchActiveTrackProtection() {
      try {
        const response = await backendCall.get('time/track-protection/active');
        console.log('[TimeTrackingStore] Track protection response:', response);
        
        if (response && response.data) {
          if (Array.isArray(response.data)) {
            this.activeTrackProtection = response.data;
            console.log('[TimeTrackingStore] Successfully fetched track protection records:', this.activeTrackProtection);
          } else {
            console.error('[TimeTrackingStore] Track protection response is not an array:', response.data);
            this.activeTrackProtection = [];
          }
        }
        return this.activeTrackProtection;
      } catch (error) {
        console.error('[TimeTrackingStore] Error fetching track protection:', error);
        this.activeTrackProtection = [];
        // Don't throw - gracefully degrade if new endpoint doesn't exist yet
        return [];
      }
    },

    async fetchActiveTimeRecords() {
      console.log('[TimeTrackingStore] Starting fetchActiveTimeRecords...');
      try {
        this.loading = true;
        
        // Fetch both endpoints in parallel for better performance
        const [timeResponse] = await Promise.all([
          backendCall.get('time/user/active'),
          this.fetchActiveTrackProtection() // Use the new method
        ]);
        
        console.log('[TimeTrackingStore] Time records response:', timeResponse);

        // Process regular time records
        if (timeResponse && timeResponse.data) {
          if (Array.isArray(timeResponse.data)) {
            // Combine regular records with track protection for backward compatibility
            this.activeTimeRecords = [...timeResponse.data, ...this.activeTrackProtection];
            console.log('[TimeTrackingStore] Combined active records:', this.activeTimeRecords);
          } else {
            // Handle nested response structure
            if (timeResponse.data.success && Array.isArray(timeResponse.data.data)) {
              this.activeTimeRecords = [...timeResponse.data.data, ...this.activeTrackProtection];
              console.log('[TimeTrackingStore] Combined active records (nested):', this.activeTimeRecords);
            } else {
              console.error('[TimeTrackingStore] Unexpected response structure:', timeResponse.data);
              this.activeTimeRecords = [...this.activeTrackProtection];
            }
          }
          // Calculate elapsed times for all records
          this.updateElapsedTimes();
        } else {
          console.error('[TimeTrackingStore] Invalid or empty response received from backend.');
          this.activeTimeRecords = [...this.activeTrackProtection];
        }
      } catch (error) {
        console.error('[TimeTrackingStore] Error fetching active time records:', error);
        this.activeTimeRecords = [];
        this.activeTrackProtection = [];
      } finally {
        this.loading = false;
        console.log('[TimeTrackingStore] fetchActiveTimeRecords finished. Current state:', this.activeTimeRecords);
      }
    },

    async endTimeRecord(
      recordId: string,
      recordType: string,
      externalToast: ReturnType<typeof useToast> | null = null,
      options?: { endTime?: string; endedRemotely?: boolean }
    ) {
      try {
        // Find the record to get additional details if needed
        const record = this.activeTimeRecords.find(r => r.id === recordId);

        let response;

        // Special handling for track protection records
        if (recordType === 'track-protection') {
          // Use the dedicated endpoint for track protection
          const trainId = record?.recordDetails?.trainId;
          console.log("Record Details:",record?.recordDetails);
          if (!trainId) {
            console.error('Cannot end track protection: missing trainId in record details');
            throw new Error('Missing trainId in record details');
          }

          // Use the same data format as in TrainTimeTrackerSlideover.vue
          const data = { type: 'train', train: trainId, tracks: [] };
          response = await backendCall.post('time/track-protection-end', data);
        } else {
          // For all other record types, use the generic endpoint
          const endpoint = `time/record/${recordId}/${recordType}/end`;
          // Include options in request body if provided
          const requestData = options ? {
            endTime: options.endTime,
            endedRemotely: options.endedRemotely
          } : undefined;
          response = await backendCall.post(endpoint, requestData);
        }
        if (response.data.success) {
          // Find the record that was ended to add to recently ended
          const endedRecord = this.activeTimeRecords.find(record => record.id === recordId);

          if (endedRecord) {
            // Add to recently ended records
            this.recentlyEndedRecords.push({
              id: recordId,
              type: endedRecord.type,
              trainId: endedRecord.recordDetails?.trainId,
              recordType: recordType,
              endedAt: new Date()
            });

            // Keep only the last 10 ended records
            if (this.recentlyEndedRecords.length > 10) {
              this.recentlyEndedRecords.shift();
            }
          }

          // Remove the ended record from the active records
          this.activeTimeRecords = this.activeTimeRecords.filter(record => record.id !== recordId);

          // Show success toast if provided
          if (externalToast) {
            let toastMessage = 'Time record has been ended successfully';

            // Provide more specific message based on record type
            if (recordType === 'wait-time') {
              toastMessage = 'Wait time record has been ended successfully';
            } else if (recordType === 'track-protection') {
              toastMessage = 'Track protection record has been ended successfully';
            } else if (recordType === 'worker-time-to-build') {
              toastMessage = 'Build time record has been ended successfully';
            }

            externalToast.add({
              severity: 'success',
              summary: 'Success',
              detail: toastMessage,
              life: 3000
            });
          }

          return true;
        }
        return false;
      } catch (error: any) {
        console.error('Error ending time record:', error);
        if (externalToast) {
          externalToast.add({
            severity: 'error',
            summary: 'Error',
            detail: error.response?.data?.error?.message || error.message || 'Failed to end time record',
            life: 3000
          });
        }
        throw error;
      }
    },

    // Action to end all track protection records for a specific train
    async endAllTrackProtectionForTrain(trainId: number, externalToast: ReturnType<typeof useToast> | null = null): Promise<boolean> {
      if (!trainId) {
        console.error('endAllTrackProtectionForTrain called with invalid trainId:', trainId);
        return false;
      }
      try {
        const response = await backendCall.post(`time/train/${trainId}/track-protection/end`);
        if (response.data.success) {
          console.log(`Successfully ended all track protection for train ${trainId}`);
          // Optionally, add details about how many records were ended from response.data.endedCount
          await this.fetchActiveTimeRecords(); // Refresh records after ending

          // Show success toast if provided
          if (externalToast) {
            externalToast.add({
              severity: 'success',
              summary: 'Success',
              detail: 'Track protection records have been ended successfully',
              life: 3000
            });
          }

          return true;
        }
        return false;
      } catch (error: any) {
        console.error(`Error ending all track protection for train ${trainId}:`, error);
        if (externalToast) {
          externalToast.add({
            severity: 'error',
            summary: 'Error',
            detail: error.response?.data?.error?.message || error.message || 'Failed to end track protection',
            life: 3000
          });
        }
        return false;
      }
    },

    startAutoRefresh(intervalSeconds: number = 30) {
      // Clear any existing interval
      this.stopAutoRefresh();

      // Set up new interval
      this.autoRefreshInterval = setInterval(() => {
        void this.fetchActiveTimeRecords();
      }, intervalSeconds * 1000);

      // Start the timer as well if we have active records
      if (this.activeTimeRecords.length > 0) {
        this.startTimer();
      }
    },

    stopAutoRefresh() {
      if (this.autoRefreshInterval) {
        clearInterval(this.autoRefreshInterval);
        this.autoRefreshInterval = null;
      }
    },

    getRecentlyEndedForTrain(trainId: number | string) {
      // Return records ended in the last 30 seconds for this train
      const thirtySecondsAgo = new Date(Date.now() - 30 * 1000);
      return this.recentlyEndedRecords.filter(record => {
        // Convert trainId to string for comparison to handle both number and string types
        const recordTrainId = String(record.trainId);
        const searchTrainId = String(trainId);

        // Convert endedAt to Date if it's not already a Date object
        const endedAtDate = record.endedAt instanceof Date ? record.endedAt : new Date(record.endedAt);

        // Use >= to include records that were ended exactly 30 seconds ago
        return recordTrainId === searchTrainId && endedAtDate >= thirtySecondsAgo;
      });
    },

    clearRecentlyEndedRecords() {
      this.recentlyEndedRecords = [];
    },

    // New methods for centralized timer management
    updateElapsedTimes() {
      const now = new Date();
      this.activeTimeRecords = this.activeTimeRecords.map(record => {
        // Only calculate elapsed time if startTime is valid
        if (!record.startTime) {
          console.warn('Record missing startTime:', record);
          return record;
        }

        try {
          const startTime = new Date(record.startTime);
          const elapsedSeconds = Math.floor((now.getTime() - startTime.getTime()) / 1000);

          // Ensure we don't have negative elapsed time (can happen if server/client times don't match)
          const safeElapsedSeconds = elapsedSeconds >= 0 ? elapsedSeconds : 0;

          return {
            ...record,
            elapsedSeconds: safeElapsedSeconds,
            elapsedTime: this.formatElapsedTime(safeElapsedSeconds)
          };
        } catch (error) {
          console.error('Error calculating elapsed time for record:', record, error);
          return record;
        }
      });
    },

    formatElapsedTime(seconds: number) {
      const hours = Math.floor(seconds / 3600);
      const minutes = Math.floor((seconds % 3600) / 60);
      const secs = seconds % 60;
      return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
    },

    startTimer() {
      this.stopTimer();
      // Immediately update elapsed times before starting the timer
      this.updateElapsedTimes();
      this.timerInterval = setInterval(() => {
        this.updateElapsedTimes();
      }, 1000);
    },

    stopTimer() {
      if (this.timerInterval) {
        clearInterval(this.timerInterval);
        this.timerInterval = null;
      }
    }
  },
}); 
